import { useState, useEffect, useMemo, useCallback } from "react";
import { getByKeyCode } from "app/services/SystemConfigService";

/**
 * A custom React hook to dynamically fetch and manage form required fields from SystemConfig.
 * 
 * @param {string} configKeyCode The system config key code (e.g. 'PATIENT_REQUIRED_FIELDS')
 * @returns {object} { requiredFields, isRequired, loading }
 */
export default function useDynamicValidation(configKeyCode) {
  const [requiredFields, setRequiredFields] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!configKeyCode) {
      setLoading(false);
      return;
    }

    setLoading(true);
    getByKeyCode(configKeyCode)
      .then((res) => {
        if (res?.data?.configValue) {
          try {
            const parsed = JSON.parse(res.data.configValue);
            setRequiredFields(parsed);
          } catch (e) {
            console.error(`Failed to parse dynamic validation config: ${configKeyCode}`, e);
          }
        }
      })
      .catch((err) => {
        console.error(`Failed to fetch dynamic validation config: ${configKeyCode}`, err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [configKeyCode]);

  /**
   * Helper to check if a specific field is configured as required.
   * 
   * @param {string} field The field name
   * @param {boolean} defaultValue Default fallback value if not specified in config
   * @returns {boolean}
   */
  const isRequired = useCallback((field, defaultValue = false) => {
    return requiredFields?.[field] ?? defaultValue;
  }, [requiredFields]);

  return useMemo(() => ({
    requiredFields,
    isRequired,
    loading
  }), [requiredFields, isRequired, loading]);
}
